import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "../axios";
import AwardsDrive from "../components/AwardsDrive";

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M7.1 3.6h2.2c.4 0 .8.3.9.7l.8 2.6c.1.4 0 .8-.3 1.1L9.2 9.4a12.6 12.6 0 0 0 5.4 5.4l1.4-1.5c.3-.3.7-.4 1.1-.3l2.6.8c.4.1.7.5.7.9v2.2c0 .5-.4.9-.9.9C11.2 17.8 6.2 12.8 6.2 4.5c0-.5.4-.9.9-.9z"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4 6.2h16c.7 0 1.2.5 1.2 1.2v9.2c0 .7-.5 1.2-1.2 1.2H4c-.7 0-1.2-.5-1.2-1.2V7.4c0-.7.5-1.2 1.2-1.2zm8 6.3 7.2-4.6H4.8L12 12.5zm0 1.5-8-5.1v7.5h16V8.9l-8 5.1z"
      />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2.8a6.2 6.2 0 0 0-6.2 6.2c0 4.6 6.2 12.2 6.2 12.2s6.2-7.6 6.2-12.2A6.2 6.2 0 0 0 12 2.8zm0 8.4a2.2 2.2 0 1 1 0-4.4 2.2 2.2 0 0 1 0 4.4z"
      />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M8 7.2V5.4C8 4.6 8.6 4 9.4 4h8.2c.8 0 1.4.6 1.4 1.4v9.2c0 .8-.6 1.4-1.4 1.4H16v1.6c0 .8-.6 1.4-1.4 1.4H6.4C5.6 19 5 18.4 5 17.6V8.6C5 7.8 5.6 7.2 6.4 7.2H8zm1.4 0h6.2c.8 0 1.4.6 1.4 1.4v6.4h1.6V5.4H9.4v1.8zM6.4 8.6v9h8.2v-9H6.4z"
      />
    </svg>
  );
}

function withPhrase(text, phrase, link) {
  const value = text || "";
  const at = value.indexOf(phrase);
  if (at < 0) {
    return value;
  }
  return (
    <>
      {value.slice(0, at)}
      {link}
      {value.slice(at + phrase.length)}
    </>
  );
}

function PurposeGallery({ photos }) {
  const [index, setIndex] = useState(0);
  const current = photos.length ? index % photos.length : 0;

  useEffect(() => {
    if (photos.length < 2) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setIndex((value) => (value + 1) % photos.length);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [photos.length, current]);

  if (!photos.length) {
    return null;
  }

  return (
    <div className="company-gallery">
      <div className="lawn-frame">
        {photos.map((photo, photoIndex) => (
          <img
            key={photo.id}
            className={photoIndex === current ? "is-on" : ""}
            src={photo.display_url}
            alt={photoIndex === current ? "Huntington Beach Library" : ""}
          />
        ))}
      </div>
      {photos.length > 1 ? (
        <div className="lawn-strip" aria-label="Library photos">
          {photos.map((photo, photoIndex) => (
            <button
              key={photo.id}
              type="button"
              className={photoIndex === current ? "is-on" : ""}
              aria-label={`Show library photo ${photoIndex + 1}`}
              title={`Show library photo ${photoIndex + 1}`}
              aria-current={photoIndex === current ? "true" : undefined}
              onClick={() => setIndex(photoIndex)}
            >
              <img src={photo.thumb_url || photo.display_url} alt="" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ContactLine({ href, value, copyLabel, icon, className }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(0);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  async function copy() {
    let ok = false;
    try {
      await navigator.clipboard.writeText(value);
      ok = true;
    } catch {
      const field = document.createElement("textarea");
      field.value = value;
      field.setAttribute("readonly", "");
      field.style.position = "fixed";
      field.style.left = "-9999px";
      document.body.appendChild(field);
      field.select();
      ok = document.execCommand("copy");
      field.remove();
    }
    if (!ok) {
      return;
    }
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="company-reach-line">
      <a className={className} href={href}>
        {icon}
        <span>{value}</span>
      </a>
      <button
        type="button"
        className={copied ? "company-copy is-copied" : "company-copy"}
        onClick={copy}
        aria-label={copied ? "Copied" : copyLabel}
        title={copied ? "Copied" : copyLabel}
      >
        {copied ? "Copied" : <CopyIcon />}
      </button>
    </div>
  );
}

const sections = [
  { id: "purpose", label: "Purpose", kicker: "Purpose", title: "Today's readers are tomorrow's leaders" },
  { id: "history", label: "History", kicker: "History highlights", title: "HB Concours d'Elegance" },
  { id: "committee", label: "Committee", kicker: "Committee", title: "HB Concours d'Elegance committee" },
  { id: "contact", label: "Contact Us", kicker: "Contact", title: "Contact Us" },
  { id: "volunteers", label: "Volunteers", kicker: "Volunteers wanted", title: "Volunteers welcome" },
];

const LIBRARY_MAP =
  "https://maps.google.com/maps?q=Huntington+Beach+Public+Library,+7111+Talbert+Avenue,+Huntington+Beach,+CA+92648&hl=en&ll=33.701199,-118.003988&z=15";

const LIBRARY_EMBED =
  "https://maps.google.com/maps?q=33.701199,-118.003988&z=15&hl=en&output=embed";

const contactPeople = [
  {
    topic: "Contact information",
    name: "Bart P. McGrath",
    role: "Founder, President",
    phone: "714.960.2100",
  },
  {
    topic: "General information",
    name: "John Bowman",
    role: "General Manager",
    email: "jtbfluke@msn.com",
  },
  {
    topic: "Automotive entries",
    name: "Bart P. McGrath",
    role: "Entries Director",
    phone: "714.960.2100",
  },
  {
    topic: "Motorcycle entries",
    name: "Dennis Crane",
    role: "Entries Director",
    email: "dennisnoreen@aol.com",
  },
  {
    topic: "Technology issues",
    name: "Klaus Lutz",
    role: "Judging Technology Director",
    email: "Klaus.Lutz@hbconcours.org",
  },
  {
    topic: "Vendors",
    name: "Bill Bergen",
    role: "Event Coordinator, food vendors",
    email: "Bill.Bergen@hbconcours.org",
  },
  {
    topic: "Judging, Sunday",
    name: "Ken Thomas",
    role: "Chief Judge",
    phone: "213.595.1545",
    phoneName: "cell phone",
    email: "kenthomas3222@outlook.com",
  },
];

const automobiles = [
  "AC Cobra", "Alfa Romeo", "Aston Martin", "Auburn", "Bentley", "BMW", "Buick", "Cadillac",
  "Chevrolet", "Chevrolet Corvette", "Chrysler", "Datsun", "Dodge", "Duesenberg", "Ferrari",
  "Ford", "Ford Mustang", "Jaguar", "Jensen", "La Salle", "Lotus", "Mercedes-Benz", "Mercury",
  "Nissan", "Oldsmobile", "Packard", "Plymouth", "Pierce-Arrow", "Pontiac", "Porsche",
  "Shelby Cobra", "Toyota and Lexus",
];

const motorcycles = [
  "Ariel", "Aprilia", "BMW", "Brough Superior", "Can-Am", "Ducati", "Francis Barnett",
  "Harley-Davidson", "Honda", "Husqvarna", "Indian", "Kawasaki", "Laverda", "Matchless",
  "Moto Guzzi", "MV Agusta", "Nimbus", "Norton", "Royal Enfield", "Suzuki", "Triumph",
  "Velocette", "Vincent", "Yamaha",
];

const committee = [
  ["Jim Ayres", "Class Manager, Lincoln. Facebook Manager.", "Jayres01@ca.rr.com"],
  ["Bill Bergen", "Member, management steering committee.", "Bill.Bergen@hbconcours.org"],
  ["John Bowman", "General Concours Manager. Class Manager, antique Fords.", "JTFLUKE@msn.com"],
  ["Dennis Crane", "Member, management steering committee. Chief Judge, motorcycles.", "dennisnoreen@aol.com"],
  ["Judith Dildine", "Member, management steering committee. Co-Class Manager, Ford.", "jkdcpa@hotmail.com"],
  ["Joe Drucker", "Awards Manager.", "joe@adavisual.com"],
  ["Alberto Ferroni", "Member, management steering committee.", "albertoferroni@verizon.net"],
  ["George Garrett", "Director, database operations. Class Manager, Ford.", "Oldford.man@verizon.net"],
  ["Ed Hennings", "Member, management steering committee.", "ehennings1@gmail.com"],
  ["William Hugron", "Class Manager, Rolls-Royce and Bentley.", "wh@calcp.com"],
  ["Klaus Lutz", "Judging Technology Director.", "Klaus.Lutz@hbconcours.org"],
  ["Chris Macha", "Co-Class Manager, BMW.", "cmacha@socal.rr.com"],
  ["Bart McGrath", "Chairman, Founder, and Entries Official.", ""],
  ["Don Nicko", "Member, management steering committee.", "KBLTransmissions@gmail.com"],
  ["Mervyn Radley", "Chief Judge, rod and custom show. Class Manager, Pontiac.", "mervynradley@juno.com"],
  ["Mykola Semenenko", "Member, management steering committee.", "Semeneko.nikolai@gmail.com"],
  ["Darrin Smith", "Member, management steering committee.", "dwsclassics@yahoo.com", "info@dwsclassics.com"],
  ["Kristi Smith", "Member, management steering committee.", "keshk1212@yahoo.com"],
  ["Ken Thomas", "Chief Judge.", "kenthomas3222@outlook.com"],
  ["Anna Zaitseva", "Awards Manager.", "referee@yahoo.com"],
];

function CompanyScreen() {
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("section");
  const section = sections.some((item) => item.id === requested) ? requested : "purpose";
  const [copy, setCopy] = useState(null);
  const banner = sections.find((item) => item.id === section);
  const shown = {
    kicker: copy?.[section]?.kicker || banner.kicker,
    title: copy?.[section]?.title || banner.title,
  };
  const [driving, setDriving] = useState(false);
  const [passer, setPasser] = useState(null);
  const driveTimer = useRef(0);
  const drivingRef = useRef(false);
  const lastPasser = useRef(null);

  useEffect(() => {
    let cancel = false;
    axios
      .get("/api/users/company/")
      .then(({ data }) => {
        if (!cancel) {
          setCopy(data);
        }
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, []);

  function beginDrive(id) {
    if (id === section || drivingRef.current) {
      return;
    }
    const choices = ["tower", "palm", "umbrella", "gull", "board"].filter((item) => item !== lastPasser.current);
    const next = choices[Math.floor(Math.random() * choices.length)];
    lastPasser.current = next;
    drivingRef.current = true;
    setPasser(next);
    setDriving(false);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setDriving(true);
        driveTimer.current = window.setTimeout(() => {
          drivingRef.current = false;
          setDriving(false);
        }, 1700);
      });
    });
  }

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  }, [section]);

  return (
    <div className="beach">
      <AwardsDrive driving={driving} passer={passer}>
        <div className="awards-car">
          <div className="awards-body">
            <div className="site-wrap awards-banner is-company">
              <div className="awards-banner-copy">
                <p className="site-kicker">{shown.kicker}</p>
                <h1>{shown.title}</h1>
              </div>
              <nav className="info-tabs tabs-5" aria-label="Company sections">
                {sections.map((item) => (
                  <Link
                    key={item.id}
                    to={`/company-information?section=${item.id}`}
                    className={section === item.id ? "is-on" : ""}
                    aria-current={section === item.id ? "page" : undefined}
                    onClick={() => beginDrive(item.id)}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
          <div className="awards-wheels" aria-hidden="true">
            <span />
            <span />
          </div>
        </div>
      </AwardsDrive>

      {section === "volunteers" ? (
      <section className="site-section company-section">
        <div className="site-wrap">
          <article className="company-card company-card-wide">
            <header className="company-card-head">
              <p className="site-kicker">{copy ? copy.volunteers.kicker : "Volunteers wanted"}</p>
              <h2>{copy ? copy.volunteers.title : "Volunteers welcome"}</h2>
            </header>
            <div className="company-card-body">
            <div className="company-intro">
              <p className="company-lead">
                {copy
                  ? copy.volunteers.lead
                  : "The Huntington Beach Concours d'Elegance celebrates its 40th anniversary in 2026. The show exists through volunteers who have taken on the work of producing one of the finer automobile and motorcycle shows on the west coast."}
              </p>
              <div className="company-reach">
                {(copy ? copy.volunteers.phone : "714.960.2100") ? (
                <ContactLine
                  href={`tel:${(copy ? copy.volunteers.phone : "714.960.2100").replace(/[^\d+]/g, "")}`}
                  value={copy ? copy.volunteers.phone : "714.960.2100"}
                  copyLabel="Copy phone number"
                  className="company-reach-num"
                  icon={<PhoneIcon />}
                />
                ) : null}
                {(copy ? copy.volunteers.email : "Diana.mcgrath@ingrammicro.com") ? (
                <ContactLine
                  href={`mailto:${copy ? copy.volunteers.email : "Diana.mcgrath@ingrammicro.com"}`}
                  value={copy ? copy.volunteers.email : "Diana.mcgrath@ingrammicro.com"}
                  copyLabel="Copy email address"
                  icon={<MailIcon />}
                />
                ) : null}
              </div>
            </div>
            <div className="company-points">
              {(copy
                ? copy.volunteers.notes
                : [
                    "The event has grown, and it takes more people to look after every detail. Sponsors, vendors, vehicle owners, spectators, and judges all have to be invited and kept in the loop. The city side is licensing, permits, and insurance.",
                    "On the days of the show, the jobs run from an information booth to checking in automobiles, and to keeping the ducks in the lake where they belong.",
                    "The committee invites anyone who wants to help the show keep growing. For more on how to assist, call Diana McGrath.",
                  ]
              ).map((note, index) => (
                <p key={index}>{note}</p>
              ))}
            </div>
            <div className="company-charity">
              <h3>{copy ? copy.volunteers.charityTitle : "This is a charitable event."}</h3>
              <p>
                {withPhrase(
                  copy
                    ? copy.volunteers.charityBody
                    : "Profits from the show are donated to the Children's Resource Center at the Huntington Beach Library.",
                  "Children's Resource Center",
                  <a
                    className="purpose-text-link"
                    href="https://hbpl.libguides.com/kids"
                    target="_blank"
                    rel="noreferrer"
                    title="Children's Resource Center"
                  >
                    Children's Resource Center
                  </a>
                )}
              </p>
            </div>
            </div>
          </article>
        </div>
      </section>
      ) : null}

      {section === "purpose" ? (
      <section className="site-section company-section">
        <div className="site-wrap">
          <article className="company-card company-card-wide">
            <header className="company-card-head">
              <p className="site-kicker">{copy ? copy.purpose.kicker : "Purpose"}</p>
              <h2>{copy ? copy.purpose.title : "Today's readers are tomorrow's leaders"}</h2>
            </header>
            <div className="company-card-body">
            <div className={(copy?.purpose.photos || []).length ? "company-purpose-open" : undefined}>
            <div>
            <p className="company-lead">
              {copy
                ? copy.purpose.lead
                : "The Huntington Beach Concours gives automotive enthusiasts another chance to see the cars and motorcycles they love. It has also been a steady fundraiser for the Children's Resource Center wing at the Huntington Beach Public Library."}
            </p>
            <div className={(copy?.purpose.photos || []).length ? "company-split company-purpose-notes" : "company-split"}>
              <p>
                {withPhrase(
                  copy
                    ? copy.purpose.left
                    : "The show is run by volunteers who are not paid. Since it was founded in 1985, it has raised over $900,000 for the library: learning services, computers, books, and other items for children of every ability, including gifted children and children with physical challenges.",
                  "volunteers",
                  <Link className="purpose-text-link" to="/company-information?section=volunteers" title="volunteers">
                    volunteers
                  </Link>
                )}
              </p>
              <p>
                {withPhrase(
                  copy
                    ? copy.purpose.right
                    : "The Children's Resource Center is ranked as one of the top children's libraries in California. Proceeds support a growing collection of books, periodicals, and other resources, plus children's theater, study labs, and staff trained to help children.",
                  "The Children's Resource Center",
                  <a
                    className="purpose-text-link"
                    href="https://hbpl.libguides.com/kids"
                    target="_blank"
                    rel="noreferrer"
                    title="The Children's Resource Center"
                  >
                    The Children's Resource Center
                  </a>
                )}
              </p>
            </div>
            </div>
            <PurposeGallery photos={copy?.purpose.photos || []} />
            </div>
            </div>
          </article>
        </div>
      </section>
      ) : null}

      {section === "history" ? (
      <section className="site-section company-section">
        <div className="site-wrap">
          <article className="company-card company-card-wide">
            <header className="company-card-head">
              <p className="site-kicker">{copy ? copy.history.kicker : "History highlights"}</p>
              <h2>{copy ? copy.history.title : "HB Concours d'Elegance"}</h2>
            </header>
            <div className="company-card-body">
            <p className="company-date">
              {copy
                ? copy.history.date
                : "The 2026 shows are Saturday, June 6 and Sunday, June 7, at Huntington Beach Central Park, on the grounds of the Huntington Beach Central Library."}
            </p>
            <div className="company-block">
              <h3>{copy ? copy.history.whyHeading : "Why the weekend exists"}</h3>
              <p className="company-lead">
                {copy
                  ? copy.history.whyLead
                  : "This annual weekend was founded in 1985 to raise funds for children's literacy. The volunteers' funds go to the Children's Resource Center at the main Huntington Beach Public Library. In 40 years the show has provided over $1.3 million."}
              </p>
              <div className="company-points">
                {(copy
                  ? copy.history.whyNotes
                  : [
                      "For 20 years the work was with the Library Patrons Foundation. After that group was dissolved, the Huntington Beach Concours d'Elegance organization was formed.",
                      "The event is held on the first full weekend in June as a judged series for automobiles, trucks, motorcycles, scooters, and bicycles. The funds helped build the expanded children's library, now the Children's Resource Center.",
                      "As a 501(c)(3) corporation, the Huntington Beach Concours has paid over the years for computers, media equipment, books, periodicals, replacement bookshelves, desks, and chairs, and for training specialized library staff who provide learning services to children.",
                    ]
                ).map((note, index) => (
                  <p key={index}>{note}</p>
                ))}
              </div>
              <p className="company-close">
                {copy
                  ? copy.history.whyClose
                  : "That support is ongoing. The annual Concours and car show help keep the Children's Resource Center an engaging place where children can learn, thrive, and prepare for the future. The Huntington Beach Children's Resource Center Library has on occasion been voted the No. 1 children's library in California, and No. 2 in the nation, second only to the City of Chicago."}
              </p>
            </div>
            <div className="company-block">
              <h3>{copy ? copy.history.yearsHeading : "Over the years"}</h3>
              <div className="company-split">
                <p>
                  {copy
                    ? copy.history.yearsLeft
                    : "The first event was held in the Charter Center complex in Huntington Beach. Frank Mola, owner of Mola Construction Company, which built Charter Center, let the Concours use the facility in June 1986. About 100 automobiles, trucks, and motorcycles took part. The field already mixed domestic classics, European classics, and domestic and imported motorcycles."}
                </p>
                <p>
                  {copy
                    ? copy.history.yearsRight
                    : "Huntington Beach Mayor Jack Kelly presented the trophies. He asked founder Bart McGrath why the event was not in Huntington Beach Central Park. Mr. McGrath said that had not worked out for the first year, and that he hoped the second year could be in the park. Mayor Kelly helped, and the event has been held in Central Park from 1987 to date."}
                </p>
              </div>
              <p className="marque-label">{copy ? copy.history.autosLabel : "Automobiles and trucks shown across those years include:"}</p>
              <div className="marque-cloud">
                {(copy ? copy.history.automobiles : automobiles).map((name) => <span key={name}>{name}</span>)}
              </div>
              <p className="marque-label">{copy ? copy.history.motosLabel : "Motorcycles shown across those years include:"}</p>
              <div className="marque-cloud">
                {(copy ? copy.history.motorcycles : motorcycles).map((name) => <span key={name}>{name}</span>)}
              </div>
              <div className="company-points">
                {(copy
                  ? copy.history.notes
                  : [
                      "There is a large live model train display for the kids.",
                      "Food, drink, and snacks are available both days. A VIP sit-down luncheon is available on both days.",
                      "Admission tickets for the weekend are sold online.",
                    ]
                ).map((note, index) => (
                  <p key={index}>{note}</p>
                ))}
              </div>
            </div>
            </div>
          </article>
        </div>
      </section>
      ) : null}

      {section === "contact" ? (
      <section className="site-section company-section">
        <div className="site-wrap">
          <article className="company-card company-card-wide">
            <header className="company-card-head">
              <p className="contact-org">Huntington Beach Concours d'Elegance</p>
              <h2>Contact Us</h2>
            </header>
            <div className="company-card-body">
              <div className="contact-layout">
                <div className="contact-list">
                  {contactPeople.map((person) => (
                    <article key={person.topic}>
                      <div>
                        <h3>{person.topic}</h3>
                        <p className="contact-name">{person.name}</p>
                        <p>{person.role}</p>
                      </div>
                      <div>
                        {person.phone ? (
                          <ContactLine
                            href={`tel:${person.phone.replace(/[^\d+]/g, "")}`}
                            value={person.phone}
                            copyLabel={`Copy ${person.name}'s ${person.phoneName || "phone number"}`}
                            icon={<PhoneIcon />}
                          />
                        ) : null}
                        {person.email ? (
                          <ContactLine
                            href={`mailto:${person.email}`}
                            value={person.email}
                            copyLabel={`Copy ${person.name}'s email`}
                            icon={<MailIcon />}
                          />
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
                <aside className="contact-inquiry">
                  <h3>General inquiry</h3>
                  <p>
                    Please send an email with your contact information (name, email and/or telephone) to us at
                  </p>
                  <ContactLine
                    href="mailto:info@hbconcours.org?subject=General%20Inquiry"
                    value="info@hbconcours.org"
                    copyLabel="Copy the general inquiry email"
                    icon={<MailIcon />}
                  />
                </aside>
              </div>
              <div className="contact-map-band">
                <iframe
                  className="contact-map-frame"
                  title="Map of Huntington Beach Public Library, 7111 Talbert Avenue"
                  src={LIBRARY_EMBED}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <div className="contact-map-card">
                  <a
                    className="contact-map"
                    href={LIBRARY_MAP}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="View map of Huntington Beach Public Library"
                    title="View map of Huntington Beach Public Library"
                  >
                    <MapIcon />
                    <span>View map</span>
                  </a>
                  <p className="contact-address">
                    <MapIcon />
                    <span>
                      Huntington Beach Concours
                      <br />
                      P.O. Box 5578
                      <br />
                      Huntington Beach, CA 92615-5578
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
      ) : null}

      {section === "committee" ? (
      <section className="site-section company-section">
        <div className="site-wrap">
          <article className="company-card company-card-wide">
            <header className="company-card-head">
              <p className="site-kicker">{copy ? copy.committee.kicker : "Committee"}</p>
              <h2>{copy ? copy.committee.title : "HB Concours d'Elegance committee"}</h2>
            </header>
            <div className="company-card-body">
            <div className="committee-grid">
              {(copy
                ? copy.committee.members
                : committee.map(([name, role, email, extra], index) => ({
                    id: index,
                    name,
                    role,
                    email,
                    extra_email: extra || "",
                  }))
              ).map((person) => (
                <article key={person.id}>
                  <h3>{person.name}</h3>
                  <p>{person.role}</p>
                  {person.email ? <a href={`mailto:${person.email}`}>{person.email}</a> : null}
                  {person.extra_email ? <a href={`mailto:${person.extra_email}`}>{person.extra_email}</a> : null}
                </article>
              ))}
            </div>
            </div>
          </article>
        </div>
      </section>
      ) : null}
    </div>
  );
}

export default CompanyScreen;
